// @ts-check
// Diálogos do editor (spec 2.0.0, fluxos.md seções 20.6 e 20.9; contrato 09 de fluxos, seção 17): salvar fluxo ativo
// com impacto (para onde vai quem está nos passos excluídos), outra pessoa salvou antes (recarregar ou criar uma cópia
// com as minhas alterações), sair sem salvar, excluir uma divisão (qual caminho fica), excluir um passo com leads e o
// e-mail que uma condição usa.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { Checkbox } from '../../ui/feedback.js';
import { formatNumber, formatTime } from '../../lib/format.js';
import { BRANCH_KEYS_BY_TYPE } from '../../../../shared/flows/flow-definition.js';
import { FlowDialog } from './flow-dialog.js';
import { FlowRadioOption } from './flow-lifecycle-dialogs.js';
import { branchLabelText } from './flow-connector.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/flows.js').FlowEditPlanDetails} FlowEditPlanDetails
 * @typedef {import('../../data/flows.js').FlowNodeCounts} FlowNodeCounts
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowNode} FlowNode
 */

/**
 * Quem está nos passos excluídos e para onde vai: por destino (id do passo novo) ou concluindo o fluxo.
 * @param {FlowEditPlanDetails} plan @param {FlowNodeCounts | null} counts
 * @returns {{ total: number, destinations: Array<{ nodeId: string | null, count: number }> }}
 */
export function impactDestinations(plan, counts) {
  /** @type {Map<string | null, number>} */
  const byDestination = new Map();
  let total = 0;
  for (const nodeId of plan.removedNodeIds ?? []) {
    const count = Number(plan.remapCounts?.[nodeId] ?? counts?.nodes?.[nodeId]?.now ?? 0);
    if (count <= 0) continue;
    const destination = plan.remap?.[nodeId] ?? null;
    byDestination.set(destination, (byDestination.get(destination) ?? 0) + count);
    total += count;
  }
  if (total === 0 && plan.affectedRuns > 0) return { total: plan.affectedRuns, destinations: [] };
  return { total, destinations: [...byDestination.entries()].map(([nodeId, count]) => ({ nodeId, count })) };
}

/**
 * "Salvar alterações no fluxo ativo".
 * @param {{ plan: FlowEditPlanDetails, counts: FlowNodeCounts | null, nodeTitle: (nodeId: string) => string, showPrime: boolean, primeCount: number | null,
 *   isBusy: boolean, onConfirm: (primeChangedEntry: boolean) => void, onClose: () => void }} props
 */
export function FlowSaveImpactModal({ plan, counts, nodeTitle, showPrime, primeCount, isBusy, onConfirm, onClose }) {
  const [prime, setPrime] = useState(false);
  const impact = impactDestinations(plan, counts);
  const parts = impact.destinations.map((destination) => (destination.nodeId
    ? t('flows.saveImpact.goesTo', { count: destination.count, number: formatNumber(destination.count), step: nodeTitle(destination.nodeId) })
    : t('flows.saveImpact.completes', { count: destination.count, number: formatNumber(destination.count) })));
  return html`<${FlowDialog} title=${t('flows.saveImpact.title')} size="medium" onClose=${onClose} testId="flow-save-impact"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.saveImpact.confirm')} accent="blue" size="small" isLoading=${isBusy} onClick=${() => onConfirm(prime)} />`}>
    ${impact.total > 0
      ? html`<p class="flow-dialog-text">${t('flows.saveImpact.removed', { count: impact.total, number: formatNumber(impact.total) })}${parts.length > 0 ? ` ${parts.join(` ${t('flows.common.and')} `)}.` : ''}</p>`
      : null}
    <p class="flow-dialog-text">${t('flows.saveImpact.waiting')}</p>
    <p class="flow-dialog-text">${t('flows.saveImpact.newSteps')}</p>
    ${showPrime
      ? html`<label class="flow-checkbox-row">
          <${Checkbox} checked=${prime} onChange=${(/** @type {boolean} */ value) => setPrime(value)} ariaLabel=${t('flows.saveImpact.prime', { number: formatNumber(primeCount ?? 0) })} />
          <span>${primeCount === null ? t('flows.saveImpact.primeUnknown') : t('flows.saveImpact.prime', { number: formatNumber(primeCount) })}</span>
        </label>`
      : null}
  <//>`;
}

/**
 * "Outra pessoa salvou este fluxo".
 * @param {{ savedBy: { name?: string | null } | null, savedAt: string | null, isBusy: boolean, onReload: () => void, onCopy: () => void, onClose: () => void }} props
 */
export function FlowConflictModal({ savedBy, savedAt, isBusy, onReload, onCopy, onClose }) {
  return html`<${FlowDialog} title=${t('flows.conflict.title')} size="small" onClose=${onClose} testId="flow-conflict" role="alertdialog"
    footer=${html`<${Button} title=${t('flows.conflict.reload')} variant="secondary" size="small" onClick=${onReload} />
      <${Button} title=${t('flows.conflict.copy')} accent="blue" size="small" isLoading=${isBusy} onClick=${onCopy} />`}>
    <p class="flow-dialog-text">${savedBy?.name && savedAt
      ? t('flows.conflict.savedBy', { name: savedBy.name, time: formatTime(savedAt) })
      : t('flows.conflict.savedBySomeone')}</p>
    <p class="flow-dialog-text">${t('flows.conflict.text')}</p>
  <//>`;
}

/**
 * "Sair sem salvar?".
 * @param {{ canSave: boolean, isBusy: boolean, onSaveAndLeave: () => void, onLeave: () => void, onClose: () => void }} props
 */
export function FlowLeaveModal({ canSave, isBusy, onSaveAndLeave, onLeave, onClose }) {
  return html`<${FlowDialog} title=${t('flows.leave.title')} size="small" onClose=${onClose} testId="flow-leave" role="alertdialog"
    footer=${html`<${Button} title=${t('flows.leave.keepEditing')} variant="tertiary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.leave.leave')} variant="secondary" accent="danger" size="small" onClick=${onLeave} />
      ${canSave ? html`<${Button} title=${t('flows.leave.saveAndLeave')} accent="blue" size="small" isLoading=${isBusy} onClick=${onSaveAndLeave} />` : null}`}>
    <p class="flow-dialog-text">${t('flows.leave.text')}</p>
  <//>`;
}

/**
 * "Excluir divisão": os dois caminhos, ou manter um deles.
 * @param {{ node: FlowNode, sizes: { total: number, byBranch: Record<string, number> }, leadsHere: number, onConfirm: (keepBranch: string | null) => void, onClose: () => void }} props
 */
export function FlowDeleteSplitModal({ node, sizes, leadsHere, onConfirm, onClose }) {
  const keys = BRANCH_KEYS_BY_TYPE[/** @type {keyof typeof BRANCH_KEYS_BY_TYPE} */ (node.type)] ?? [];
  const [choice, setChoice] = useState(/** @type {string} */ ('ALL'));
  return html`<${FlowDialog} title=${t('flows.deleteSplit.title')} size="small" onClose=${onClose} testId="flow-delete-split" role="alertdialog"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.deleteSplit.confirm')} accent="danger" size="small" onClick=${() => onConfirm(choice === 'ALL' ? null : choice)} />`}>
    <div class="flow-radio-group" role="radiogroup" aria-label=${t('flows.deleteSplit.title')}>
      <${FlowRadioOption} name="flow-delete-split" value="ALL" checked=${choice === 'ALL'} title=${t('flows.deleteSplit.all', { count: sizes.total + 1 })}
        onSelect=${() => setChoice('ALL')} />
      ${keys.map((keep) => {
        const other = keys.find((key) => key !== keep) ?? '';
        return html`<${FlowRadioOption} key=${keep} name="flow-delete-split" value=${keep} checked=${choice === keep}
          title=${t('flows.deleteSplit.keep', { keep: branchLabelText(keep), remove: branchLabelText(other) })}
          description=${t('flows.deleteSplit.keepHint', { count: sizes.byBranch[keep] ?? 0 })} onSelect=${() => setChoice(keep)} />`;
      })}
    </div>
    ${leadsHere > 0 ? html`<p class="flow-dialog-text">${t('flows.deleteSplit.leads', { count: leadsHere, number: formatNumber(leadsHere) })}</p>` : null}
  <//>`;
}

/**
 * Excluir um passo com leads nele, num fluxo ativo.
 * @param {{ count: number, nextTitle: string | null, onConfirm: () => void, onClose: () => void }} props
 */
export function FlowDeleteNodeConfirm({ count, nextTitle, onConfirm, onClose }) {
  return html`<${FlowDialog} title=${t('flows.deleteNode.title')} size="small" onClose=${onClose} testId="flow-delete-node" role="alertdialog"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.deleteNode.confirm')} accent="danger" size="small" onClick=${onConfirm} />`}>
    <p class="flow-dialog-text">${nextTitle
      ? t('flows.deleteNode.goesTo', { count, number: formatNumber(count), step: nextTitle })
      : t('flows.deleteNode.completes', { count, number: formatNumber(count) })}</p>
  <//>`;
}

/**
 * O e-mail que uma condição "abriu ou clicou" usa não pode sair.
 * @param {{ onShowCondition: () => void, onClose: () => void }} props
 */
export function FlowEmailInUseDialog({ onShowCondition, onClose }) {
  return html`<${FlowDialog} title=${t('flows.emailInUse.title')} size="small" onClose=${onClose} testId="flow-email-in-use"
    footer=${html`<${Button} title=${t('flows.common.understood')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('flows.emailInUse.show')} accent="blue" size="small" onClick=${onShowCondition} />`}>
    <p class="flow-dialog-text">${t('flows.emailInUse.text')}</p>
  <//>`;
}

